<template>
	<div class="pa-3 full-height new-back">
		<billables-nest-toolbar
			v-model="filters.search"
			placeholder="Search"
			:applied-filters="appliedFilters"
			:show-excel="false"
			:show-suggested="false"
			inline-filters
		>
			<v-autocomplete
				v-model="filters.customers"
				:items="customerOptions"
				label="Customer"
				item-text="name"
				item-value="id"
				dense outlined hide-details clearable multiple
				:menu-props="filterMenuProps"
			>
				<template #selection="{ index }">
					<span v-if="index === 0" class="bnt-select-summary">{{ multiLabel(filters.customers, 'customer') }}</span>
				</template>
			</v-autocomplete>
			<v-autocomplete
				v-model="filters.billables"
				:items="billableOptions"
				label="Billable Code"
				item-text="code"
				item-value="id"
				dense outlined hide-details clearable multiple
				:menu-props="filterMenuProps"
			>
				<template #selection="{ index }">
					<span v-if="index === 0" class="bnt-select-summary">{{ multiLabel(filters.billables, 'billable') }}</span>
				</template>
			</v-autocomplete>
			<v-select
				v-model="filters.billableTypes"
				:items="billableTypes"
				label="Type"
				item-text="code"
				item-value="id"
				dense outlined hide-details clearable multiple
				:menu-props="filterMenuProps"
			>
				<template #selection="{ index }">
					<span v-if="index === 0" class="bnt-select-summary">{{ multiLabel(filters.billableTypes, 'type') }}</span>
				</template>
			</v-select>
			<v-select
				v-model="filters.currency"
				:items="currencies"
				label="Currency"
				item-text="text"
				item-value="text"
				dense outlined hide-details clearable multiple
				:menu-props="filterMenuProps"
			>
				<template #selection="{ index }">
					<span v-if="index === 0" class="bnt-select-summary">{{ multiLabel(filters.currency, 'currency') }}</span>
				</template>
			</v-select>
		</billables-nest-toolbar>

		<div class="suggested-actions">
			<div class="suggested-actions__dates">
				<v-btn
					icon
					class="suggested-actions__date-nav"
					:disabled="!filters.startDate || !filters.endDate"
					@click="shiftRange(-1)"
				>
					<v-icon>mdi-chevron-left</v-icon>
				</v-btn>
				<q-date-picker
					v-model="filters.startDate"
					class="suggested-actions__date"
					@change="init"
					label="Start Date"
					dense outlined hide-details
					:menu-props="filterMenuProps"
				/>
				<q-date-picker
					v-model="filters.endDate"
					class="suggested-actions__date"
					@change="init"
					label="End Date"
					dense outlined hide-details
					:menu-props="filterMenuProps"
				/>
				<v-btn
					icon
					class="suggested-actions__date-nav"
					:disabled="!filters.startDate || !filters.endDate"
					@click="shiftRange(1)"
				>
					<v-icon>mdi-chevron-right</v-icon>
				</v-btn>
				<v-tooltip v-if="unbilledMonths.length" bottom max-width="280">
					<template #activator="{ on, attrs }">
						<div
							class="suggested-actions__unbilled"
							v-bind="attrs"
							v-on="on"
						>
							<span class="suggested-actions__unbilled-icon" aria-hidden="true">⚠️</span>
							<span>You have unsubmitted billable hours from previous months!</span>
						</div>
					</template>
					<div class="suggested-actions__unbilled-tip">
						<div
							v-for="month in unbilledMonths"
							:key="month"
						>
							{{ formatUnbilledMonth(month) }}
						</div>
					</div>
				</v-tooltip>
			</div>

			<div class="suggested-actions__buttons">
				<v-tooltip bottom>
					<template #activator="{ on, attrs }">
						<v-btn
							v-bind="attrs"
							v-on="on"
							class="suggested-actions__expand"
							:class="{ 'suggested-actions__expand--active': allExpanded }"
							color="primary"
							fab
							depressed
							:disabled="!shownItems.length"
							@click="toggleExpandAll"
						>
							<v-icon>{{ allExpanded ? 'mdi-unfold-less-horizontal' : 'mdi-unfold-more-horizontal' }}</v-icon>
						</v-btn>
					</template>
					<span>{{ allExpanded ? 'Collapse all' : 'Expand all' }}</span>
				</v-tooltip>

				<v-btn
					class="suggested-actions__select text-none"
					color="primary"
					outlined
					depressed
					:disabled="!hasSelectableLines"
					@click="toggleSelectAllGlobal"
				>
					{{ allSelected ? 'Deselect All' : 'Select All' }}
				</v-btn>

				<v-btn
					class="bnt-primary-btn text-none"
					color="primary"
					depressed
					@click="processBI"
				>
					Process BI
				</v-btn>
			</div>
		</div>

		<div v-if="!listSections.length" class="rounded-lg bordered new-back lighten2 pa-6 text-center text--secondary">
			No suggested billables found
		</div>

		<div
			v-for="group in listSections"
			:key="group.key"
			class="customer-group rounded-lg bordered new-back lighten2 mb-4"
		>
			<div class="customer-group__header">
				<span class="customer-group__title">{{ group.name }}</span>
			</div>

			<div class="customer-group__body">
				<div class="bi-row bi-header">
					<div class="bi-cell bi-header__cell bi-header__cell--expand"/>
					<div class="bi-cell bi-header__cell">Billable</div>
					<div class="bi-cell bi-header__cell">Start Date</div>
					<div class="bi-cell bi-header__cell">End Date</div>
					<div class="bi-cell bi-header__cell">Type</div>
					<div class="bi-cell bi-header__cell">Hours</div>
					<div class="bi-cell bi-header__cell">Billed</div>
				</div>

				<div
					v-for="item in group.items"
					:key="item.billableId"
					class="bi-billable-block"
				>
					<div
						class="bi-row my-1 bi-row--clickable"
						@click="toggleExpand(item.billableId)"
					>
						<div class="bi-cell bi-cell--expand" @click.stop>
							<v-btn icon x-small @click="toggleExpand(item.billableId)">
								<v-icon small>
									{{ isExpanded(item.billableId) ? 'mdi-chevron-down' : 'mdi-chevron-right' }}
								</v-icon>
							</v-btn>
						</div>
						<div class="bi-cell">
							<div class="bi-value font-weight-medium text-truncate" :title="item.billableCode">
								{{ item.billableCode }}
							</div>
							<div v-if="(item.customerProjects || []).length" class="bi-projects">
								<div
									v-for="project in item.customerProjects"
									:key="project.id"
									class="project"
									:style="{ background: projectStyle(project).lightColor }"
								>
									<div class="letter" :style="{ background: projectStyle(project).color }">
										{{ projectStyle(project).small }}
									</div>
									<div class="name">{{ project.name }}</div>
								</div>
							</div>
						</div>
						<div class="bi-cell">
							<div class="bi-value">{{ item.startDate | formatDate }}</div>
						</div>
						<div class="bi-cell">
							<div class="bi-value">{{ item.endDate | formatDate }}</div>
						</div>
						<div class="bi-cell">
							<div class="bi-value">{{ typeCode(item.billableType) }}</div>
						</div>
						<div class="bi-cell">
							<div class="bi-value">{{ getHours(item) | number(2) }}</div>
						</div>
						<div class="bi-cell">
							<div class="bi-value bi-value--billed">
								<v-avatar size="18" class="bi-currency-flag">
									<v-img :src="currencyFlag(item.currency)" />
								</v-avatar>
								<span>${{ getBilled(item) | money }}</span>
								<span class="bi-currency-code">{{ item.currency || 'USD' }}</span>
							</div>
						</div>
					</div>

					<div v-if="isExpanded(item.billableId)" class="suggested-lines">
						<div class="suggested-lines__header">
							<div class="suggested-lines__cell suggested-lines__cell--check"/>
							<div class="suggested-lines__cell">Assignee</div>
							<div class="suggested-lines__cell">Periods</div>
							<div class="suggested-lines__cell">Total Hours</div>
							<div class="suggested-lines__cell">{{ item.billableType === 0 ? 'Unitary Cost' : 'Rate per hour' }}</div>
							<div class="suggested-lines__cell">Total Amount</div>
						</div>

						<div
							v-for="assignee in assigneeGroups(item)"
							:key="assignee.key"
							class="assignee-block"
						>
							<div
								v-for="(row, rowIx) in assignee.statusRows"
								:key="`${assignee.key}|${row.type}`"
								class="suggested-lines__row suggested-lines__row--assignee"
								:class="{ 'suggested-lines__row--status-follow': rowIx > 0 }"
							>
								<div class="suggested-lines__cell suggested-lines__cell--check" @click.stop>
									<v-checkbox
										v-if="rowIx === 0 && assignee.selectable.length"
										:input-value="isAssigneeSelected(item, assignee)"
										:indeterminate="isAssigneePartial(item, assignee)"
										@change="toggleAssignee(item, assignee, $event)"
										hide-details
										dense
										class="ma-0 pa-0"
									/>
								</div>
								<div class="suggested-lines__cell suggested-lines__cell--assignee">
									<template v-if="rowIx === 0">
										<user-avatar
											v-if="assignee.userId"
											:user-id="assignee.userId"
											:size="28"
											:show-card="false"
											hide-loading
											class="assignee-avatar"
										/>
										<div class="assignee-meta">
											<span class="assignee-name">{{ assignee.userBlueTag || '—' }}</span>
											<span v-if="assignee.seat" class="assignee-seat">{{ assignee.seat }}</span>
										</div>
									</template>
								</div>
								<div class="suggested-lines__cell">
									<div class="period-chips">
										<span
											v-for="line in row.lines"
											:key="line._key"
											class="period-chip"
											:class="{
												'period-chip--billable': line.type === 0,
												'period-chip--billed': line.type === 2,
												'period-chip--off': line.type === 1,
											}"
											:title="periodTitle(line, item)"
										>
											<span class="period-chip__dates">{{ periodChipLabel(line) }}</span>
											<span class="period-chip__badge">{{ periodChipBadge(line) }}</span>
										</span>
									</div>
								</div>
								<div class="suggested-lines__cell">{{ row.hours | number(2) }} hrs</div>
								<div class="suggested-lines__cell">${{ row.rate | money }}</div>
								<div
									class="suggested-lines__cell amount-value"
									:class="{
										'amount-value--ok': row.type === 0,
										'amount-value--billed': row.type === 2,
										'amount-value--off': row.type === 1,
									}"
								>
									${{ row.total | money }}
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>

		<blue-confirm-dialog ref="processDialog">
			<template #title>
				<div class="d-flex justify-space-between flex align-center pr-8">
					<div class="d-flex flex-column">
						<span class="primary--text">Confirm Billable Items</span>
						<span class="body-1">
							{{ processCreated
								? `${billableItems.length} items created`
								: `${billableItems.length} items selected` }}
						</span>
					</div>
					<div class="d-flex align-center" style="gap: 12px">
						<div class="subtitle-1">{{ rangeLabel }}</div>
						<v-btn icon @click="closeProcessDialog">
							<v-icon>mdi-close</v-icon>
						</v-btn>
					</div>
				</div>
			</template>
			<template #text>
				<div class="d-flex flex-column gap-5">
					<v-expand-transition v-for="(item, ix) in billableItems" :key="ix">
						<v-expansion-panels v-model="item.expanded" flat class="confirm-bi-panel">
							<v-expansion-panel class="bordered darken rounded-lg overflow-hidden">
								<v-expansion-panel-header color="newDesignBackground">
									<div class="d-flex justify-space-between align-center flex">
										<h3 class="d-flex align-center mb-0" style="gap: 6px">
											{{ item.code || item.billableCode }}
											<template v-if="item.saved">
												<v-icon color="success">mdi-checkbox-marked-circle</v-icon>
												<copy-link-button :link="`/admin/billableItem/${item.id}`" />
											</template>
										</h3>
										<div class="d-flex align-center">
											<v-avatar color="indigo" size="22" class="mr-1">
												<v-img :src="currencyFlag(item.billableCurrency)" />
											</v-avatar>
											<span class="body-1">{{ item.billableCurrency }}</span>
										</div>
									</div>
								</v-expansion-panel-header>
								<v-expansion-panel-content class="pt-3" color="newDesignBackground">
									<div class="confirm-bi-meta mb-3">
										<div>
											<div class="confirm-bi-meta__label">Start date</div>
											<div>{{ item.startDate | formatDate }}</div>
										</div>
										<div>
											<div class="confirm-bi-meta__label">End date</div>
											<div>{{ item.endDate | formatDate }}</div>
										</div>
										<div class="text-center">
											<div class="confirm-bi-meta__label">Currency</div>
											<div class="d-flex align-center justify-center">
												<v-avatar color="indigo" size="28" class="mr-1">
													<v-img :src="currencyFlag(item.billableCurrency)" />
												</v-avatar>
												<span>{{ item.billableCurrency }}</span>
											</div>
										</div>
									</div>
									<v-data-table
										:headers="billableItemLineHeaders"
										:items="item.lines"
										hide-default-footer
										disable-pagination
										item-key="billableLineId"
										class="newDesignBackground"
									>
										<template #[`header.rate`]>
											{{ item.billableType === 0 ? 'Unitary Rate' : 'Rate per Hour' }}
										</template>
										<template #[`header.quantity`]>
											{{ item.billableType === 0 ? 'Quantity' : 'Hours' }}
										</template>
										<template #[`item.billableLineRole`]="{ item: line }">
											{{ line.billableLineRole || '—' }}
										</template>
										<template #[`item.rate`]="{ item: line }">
											${{ line.rate | money }}
										</template>
										<template #[`item.quantity`]="{ item: line }">
											{{ line.quantity | number(2) }}
										</template>
										<template #[`item.total`]="{ item: line }">
											${{ line.total | money }}
										</template>
									</v-data-table>
									<div v-if="item.saved" class="created-banner mt-3">
										<v-icon color="success" small class="mr-1">mdi-checkbox-marked-circle</v-icon>
										Billable Item Created
									</div>
								</v-expansion-panel-content>
							</v-expansion-panel>
						</v-expansion-panels>
					</v-expand-transition>
				</div>
			</template>
			<template #actions>
				<v-spacer />
				<template v-if="!processCreated">
					<v-btn text :disabled="processCreating" @click="closeProcessDialog">Cancel</v-btn>
					<v-btn color="primary" text :loading="processCreating" @click="createProcessedItems">Create</v-btn>
				</template>
				<v-btn v-else color="primary" text @click="closeProcessDialog">Close</v-btn>
			</template>
		</blue-confirm-dialog>
	</div>
</template>

<script>
import moment from 'moment'
import BillableJSON from '../../json/billable.json'
import BillablesNestToolbar from '../../components/admin-surf/billables-nest/BillablesNestToolbar.vue'
import CopyLinkButton from '../../components/CopyLinkButton.vue'

function defaultMonthRange() {
	return {
		startDate: moment().startOf('month').format('YYYY-MM-DD'),
		endDate: moment().endOf('month').format('YYYY-MM-DD'),
	}
}

export default {
	name: 'SuggestedBillables',
	components: { BillablesNestToolbar, CopyLinkButton },
	data() {
		const range = defaultMonthRange()
		return {
			items: [],
			unbilledMonths: [],
			billableTypes: [],
			projectTypes: [],
			billableItems: [],
			processCreated: false,
			processCreating: false,
			expandedIds: {},
			currencies: [{ text: 'MXN' }, { text: 'USD' }, { text: 'EUR' }],
			filterMenuProps: {
				offsetY: true,
				bottom: true,
				nudgeBottom: 4,
				allowOverflow: true,
				closeOnContentClick: false,
				contentClass: 'bnt-filter-menu',
			},
			filters: {
				search: '',
				customers: [],
				billables: [],
				billableTypes: [],
				currency: [],
				startDate: range.startDate,
				endDate: range.endDate,
			},
			billableItemLineHeaders: [
				{ value: 'billableLineRole', text: 'Seat' },
				{ value: 'rate', text: 'Rate per hour', align: 'right' },
				{ value: 'quantity', text: 'Quantity', align: 'right' },
				{ value: 'total', text: 'Total', align: 'right' },
			],
			defaultProjectType: { color: '#757575', lightColor: '#eeeeee', small: '?' },
		}
	},
	created() {
		document.title = 'Suggested Billable Items'
		this.projectTypes = BillableJSON.projectTypes
		this.init()
		this.loadUnbilledMonths()
	},
	computed: {
		appliedFilters() {
			return this.countActiveFilters(this.filters, ['search', 'startDate', 'endDate'])
		},
		rangeLabel() {
			return `${moment(this.filters.startDate).format('MMM DD, YYYY')} - ${moment(this.filters.endDate).format('MMM DD, YYYY')}`
		},
		shownItems() {
			let items = this.items || []
			if (this.filters.search) {
				items = this.applySearch(this.filters.search, items)
			}
			if (this.filters.customers.length) {
				items = items.filter(item => this.filters.customers.includes(item.customerId))
			}
			if (this.filters.billables.length) {
				items = items.filter(item => this.filters.billables.includes(item.billableId))
			}
			if (this.filters.billableTypes.length) {
				items = items.filter(item => this.filters.billableTypes.includes(item.billableType))
			}
			if (this.filters.currency.length) {
				items = items.filter(item => this.filters.currency.includes(item.currency))
			}
			return items
		},
		customerOptions() {
			const list = (this.items || []).map(item => ({ id: item.customerId, name: item.customerName }))
			return list.filter((c, ix) => list.findIndex(x => x.id === c.id) === ix)
				.sort((a, b) => (a.name || '').localeCompare(b.name || ''))
		},
		billableOptions() {
			const list = (this.items || []).map(item => ({ id: item.billableId, code: item.billableCode }))
			return list.filter((b, ix) => list.findIndex(x => x.id === b.id) === ix)
				.sort((a, b) => (a.code || '').localeCompare(b.code || ''))
		},
		listSections() {
			const map = {}
			this.shownItems.forEach(item => {
				const key = item.customerId || 'unknown'
				if (!map[key]) {
					map[key] = {
						key,
						name: item.customerName || 'Unknown',
						items: [],
					}
				}
				map[key].items.push(item)
			})
			return Object.values(map).sort((a, b) => a.name.localeCompare(b.name))
		},
		allExpanded() {
			const items = this.shownItems
			return items.length > 0 && items.every(item => this.isExpanded(item.billableId))
		},
		hasSelectableLines() {
			return this.shownItems.some(item => this.selectableLines(item).length > 0)
		},
		allSelected() {
			const items = this.shownItems.filter(item => this.selectableLines(item).length > 0)
			return items.length > 0 && items.every(item => this.isAllLinesSelected(item))
		},
	},
	methods: {
		shiftRange(months) {
			if (!this.filters.startDate || !this.filters.endDate) return
			this.filters.startDate = moment(this.filters.startDate).add(months, 'month').format('YYYY-MM-DD')
			this.filters.endDate = moment(this.filters.endDate).add(months, 'month').format('YYYY-MM-DD')
			this.init()
		},
		loadUnbilledMonths() {
			this.$http.get('api/billableItem/suggested/unbilledMonths', {
				headers: { hideLoading: true },
			})
				.then(res => {
					this.unbilledMonths = res.data || []
				})
				.catch(error => {
					console.error(error)
					this.unbilledMonths = []
				})
		},
		formatUnbilledMonth(month) {
			return moment(month, 'YYYY-MM').format('MMM-YYYY')
		},
		async init() {
			if (!this.filters.startDate || !this.filters.endDate) return
			if (moment(this.filters.endDate).isBefore(this.filters.startDate, 'day')) {
				this.$root.error('End Date must be on or after Start Date')
				return
			}

			this.$http.post('api/billableItem/suggested', {
				startDate: this.filters.startDate,
				endDate: this.filters.endDate,
			})
				.then(res => {
					const items = res.data || []
					items.forEach(item => {
						this.$set(item, 'selectedLines', [])
						;(item.lines || []).forEach(line => {
							this.$set(line, '_key', `${line.assignmentId || ''}|${line.userId || ''}|${line.startDate}|${line.billableLineId || ''}|${line.type}`)
							this.$set(line, 'isSelectable', line.type === 0)
						})
					})
					this.items = items
					const expanded = {}
					items.forEach(item => {
						expanded[item.billableId] = true
					})
					this.expandedIds = expanded
				})
				.catch(error => {
					console.error(error)
					this.$root.error('Failed to fetch suggested billable items')
				})

			this.$http.get('api/enums/admin/billable/type')
				.then(res => { this.billableTypes = res.data || [] })
				.catch(() => this.$root.error('Failed to fetch billable types'))
		},
		currencyFlag(currency) {
			const code = (currency || 'USD').toLowerCase()
			return require(`../../assets/currencies/${code}.png`)
		},
		multiLabel(values, kind) {
			const selected = values || []
			if (!selected.length) return ''
			if (selected.length === 1) {
				if (kind === 'customer') {
					const match = this.customerOptions.find(c => c.id === selected[0])
					return match ? match.name : '1 selected'
				}
				if (kind === 'billable') {
					const match = this.billableOptions.find(b => b.id === selected[0])
					return match ? match.code : '1 selected'
				}
				if (kind === 'type') {
					const match = this.billableTypes.find(t => t.id === selected[0])
					return match ? match.code : '1 selected'
				}
				if (kind === 'currency') return selected[0]
				return '1 selected'
			}
			return `${selected.length} selected`
		},
		projectStyle(project) {
			const found = (this.projectTypes || []).find(t => t.value == project.type)
			return found || this.defaultProjectType
		},
		periodChipLabel(line) {
			const start = moment(line.startDate).format('MMM D')
			const end = moment(line.endDate).format('MMM D, YYYY')
			return `${start} – ${end}`
		},
		periodChipBadge(line) {
			if (line.type === 2) return 'Billed'
			if (line.type === 1) return 'Non Billable'
			return 'Billable'
		},
		periodTitle(line, item) {
			const hours = Number(line.quantity || 0).toFixed(2)
			const amount = Number(line.total || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
			const currency = item.currency || 'USD'
			const kind = this.periodChipBadge(line)
			return `${kind} · ${hours} hrs · $${amount} ${currency}`
		},
		typeCode(typeId) {
			const match = this.billableTypes.find(t => t.id === typeId)
			return match ? match.code : (['FxR', 'RPAH', 'RPEH', 'Int'][typeId] || '—')
		},
		assigneeGroups(item) {
			const map = {}
			;(item.lines || []).forEach(line => {
				// Keep same person+seat together even if billed lines omit assignmentId
				const key = `${line.userId || 'none'}|${line.billableLineId || line.billableLineRole || 'seat'}`
				if (!map[key]) {
					map[key] = {
						key,
						userId: line.userId,
						userBlueTag: line.userBlueTag,
						seat: line.billableLineRole,
						lines: [],
					}
				}
				map[key].lines.push(line)
				if (!map[key].userBlueTag && line.userBlueTag) map[key].userBlueTag = line.userBlueTag
				if (!map[key].userId && line.userId) map[key].userId = line.userId
				if (!map[key].seat && line.billableLineRole) map[key].seat = line.billableLineRole
			})

			// Billed → To bill → Non billed (one row each, assignee shown once)
			const typeOrder = [2, 0, 1]

			return Object.values(map).map(group => {
				const byType = {}
				group.lines.forEach(line => {
					const t = line.type
					if (!byType[t]) byType[t] = []
					byType[t].push(line)
				})

				const statusRows = typeOrder
					.filter(t => byType[t]?.length)
					.map(t => {
						const lines = byType[t]
							.slice()
							.sort((a, b) => moment(a.startDate).valueOf() - moment(b.startDate).valueOf())
						return {
							type: t,
							lines,
							hours: lines.reduce((acc, l) => acc + (l.quantity || 0), 0),
							total: lines.reduce((acc, l) => acc + (l.total || 0), 0),
							rate: lines[0]?.rate || 0,
							selectable: lines.filter(l => l.isSelectable),
						}
					})

				return {
					...group,
					statusRows,
					selectable: group.lines.filter(l => l.isSelectable),
				}
			})
		},
		toggleExpand(id) {
			this.$set(this.expandedIds, id, !this.expandedIds[id])
		},
		isExpanded(id) {
			return !!this.expandedIds[id]
		},
		toggleExpandAll() {
			const expand = !this.allExpanded
			this.shownItems.forEach(item => {
				this.$set(this.expandedIds, item.billableId, expand)
			})
		},
		toggleSelectAllGlobal() {
			const select = !this.allSelected
			this.shownItems.forEach(item => {
				this.toggleSelectAll(item, select)
			})
		},
		isLineSelected(item, line) {
			return (item.selectedLines || []).some(l => l._key === line._key)
		},
		selectableLines(item) {
			return (item.lines || []).filter(line => line.isSelectable)
		},
		isAllLinesSelected(item) {
			const selectable = this.selectableLines(item)
			return selectable.length > 0 && selectable.every(line => this.isLineSelected(item, line))
		},
		isSomeLinesSelected(item) {
			return this.selectableLines(item).some(line => this.isLineSelected(item, line))
		},
		isAssigneeSelected(item, assignee) {
			return assignee.selectable.length > 0
				&& assignee.selectable.every(line => this.isLineSelected(item, line))
		},
		isAssigneePartial(item, assignee) {
			const selected = assignee.selectable.filter(line => this.isLineSelected(item, line))
			return selected.length > 0 && selected.length < assignee.selectable.length
		},
		toggleAssignee(item, assignee, checked) {
			const selected = (item.selectedLines || []).slice()
			assignee.selectable.forEach(line => {
				const ix = selected.findIndex(l => l._key === line._key)
				if (checked && ix < 0) selected.push(line)
				if (!checked && ix >= 0) selected.splice(ix, 1)
			})
			this.$set(item, 'selectedLines', selected)
		},
		toggleSelectAll(item, checked) {
			this.$set(item, 'selectedLines', checked ? this.selectableLines(item).slice() : [])
		},
		toggleLine(item, line, checked) {
			const selected = item.selectedLines || []
			if (checked) {
				if (!this.isLineSelected(item, line)) {
					selected.push(line)
				}
			} else {
				const ix = selected.findIndex(l => l._key === line._key)
				if (ix >= 0) selected.splice(ix, 1)
			}
			this.$set(item, 'selectedLines', selected.slice())
		},
		getHours(item) {
			return item.selectedLines?.reduce((acc, line) => acc + line.quantity, 0) || 0
		},
		getBilled(item) {
			return item.selectedLines?.reduce((acc, line) => acc + line.total, 0) || 0
		},
		async processBI() {
			const selectedItems = structuredClone(this.shownItems.filter(item => item.selectedLines?.length > 0))
			if (!selectedItems.length) {
				this.$root.error('No billable items selected')
				return
			}
			selectedItems.forEach(item => {
				item.lines = item.selectedLines
				item.startDate = this.filters.startDate
				item.endDate = this.filters.endDate
			})

			try {
				const res = await this.$http.post('api/billableItem/suggested/build', selectedItems)
				this.billableItems = (res.data || []).map((x, i) => ({
					...x,
					expanded: i === 0 ? 0 : undefined,
					saved: false,
				}))
				this.processCreated = false
				this.processCreating = false
				await this.$refs.processDialog.open({ width: 700 })
				if (this.processCreated) this.init()
			} catch (error) {
				console.error(error)
				this.$root.error('Failed to build billable items')
			}
		},
		async createProcessedItems() {
			if (this.processCreating || this.processCreated) return
			this.processCreating = true
			const emptyGuid = '00000000-0000-0000-0000-000000000000'
			try {
				const payload = this.billableItems.map(({ saved, expanded, ...item }) => item)
				const created = await this.$http.post('api/billableItem/bulk', payload)
				const createdItems = Array.isArray(created.data) ? created.data : []
				this.billableItems = this.billableItems.map((item, i) => {
					const createdItem = createdItems[i]
						|| createdItems.find(x => x.code && x.code === item.code)
						|| {}
					const id = createdItem.id && createdItem.id !== emptyGuid
						? createdItem.id
						: item.id
					return {
						...item,
						id,
						code: createdItem.code || item.code,
						saved: true,
						expanded: i === 0 ? 0 : item.expanded,
					}
				})
				this.processCreated = true
			} catch (error) {
				console.error(error)
				this.$root.error('Failed to create billable items')
			} finally {
				this.processCreating = false
			}
		},
		closeProcessDialog() {
			this.$refs.processDialog.close(this.processCreated)
		},
	},
}
</script>

<style lang="scss" scoped>
.bnt-primary-btn {
	border-radius: 10px !important;
	height: 40px !important;
	padding: 0 16px !important;
	font-weight: 600;
	letter-spacing: 0;
	box-shadow: none !important;
	white-space: nowrap;
}

.suggested-actions {
	display: flex;
	align-items: center;
	justify-content: space-between;
	flex-wrap: wrap;
	gap: 10px;
	margin: 0 0 16px;
}

.suggested-actions__dates {
	display: flex;
	align-items: center;
	flex-wrap: wrap;
	gap: 10px;
}

.suggested-actions__date-nav {
	flex: 0 0 auto;
	margin: 0;
	color: var(--v-newDesignText-base, #8b93a0) !important;
}

.suggested-actions__unbilled {
	display: flex;
	align-items: center;
	gap: 6px;
	max-width: 420px;
	margin-left: 4px;
	color: var(--v-error-base);
	font-size: 13px;
	font-weight: 600;
	line-height: 1.3;
	cursor: default;
}

.suggested-actions__unbilled-icon {
	flex: 0 0 auto;
	font-size: 14px;
	line-height: 1;
}

.suggested-actions__unbilled-tip {
	text-align: center;
	line-height: 1.5;
}

.suggested-actions__date {
	flex: 0 0 180px;
	width: 180px;
	min-width: 180px;
	max-width: 180px;
	margin: 0;

	::v-deep .v-input__slot {
		background: var(--v-newDesignBackground-lighten2) !important;
		border: 1px solid var(--v-newDesignDivider-base, #d7dbe0) !important;
		border-radius: 10px !important;
		box-shadow: none !important;
		min-height: 40px !important;
		height: 40px !important;
		display: flex !important;
		align-items: center !important;
	}

	::v-deep .v-input__control {
		min-height: 40px !important;
	}

	::v-deep .v-text-field--outlined fieldset {
		border: none !important;
	}

	::v-deep .v-label {
		font-size: 0.85rem;
		color: var(--v-newDesignText-secondary, #8b93a0) !important;
	}

	::v-deep input,
	::v-deep .v-select__selection {
		color: var(--v-newDesignText-base) !important;
	}
}

.suggested-actions__buttons {
	display: flex;
	align-items: center;
	justify-content: flex-end;
	flex-wrap: wrap;
	gap: 10px;
	margin-left: auto;
}

.suggested-actions__expand {
	width: 40px !important;
	height: 40px !important;
	min-width: 40px !important;
	box-shadow: none !important;
	margin: 0 !important;

	&--active {
		filter: brightness(0.92);
	}
}

.suggested-actions__select {
	border-radius: 10px !important;
	height: 40px !important;
	padding: 0 16px !important;
	font-weight: 600;
	letter-spacing: 0;
	box-shadow: none !important;
	white-space: nowrap;
}

.theme--dark .suggested-actions__select {
	color: #fff !important;
	caret-color: #fff !important;

	::v-deep .v-btn__content {
		color: #fff !important;
	}
}

.theme--dark .suggested-actions__date {
	::v-deep .v-label {
		color: rgba(255, 255, 255, 0.85) !important;
	}

	::v-deep input,
	::v-deep .v-select__selection {
		color: #fff !important;
	}
}

.customer-group {
	overflow: hidden;
	background: var(--v-newDesignBackground-base);
	box-shadow: none;

	&__header {
		display: flex;
		align-items: center;
		gap: 8px;
		padding: 12px 16px 8px;
	}

	&__title {
		font-weight: 700;
		font-size: 1.05rem;
		line-height: 1.2;
		color: var(--v-newDesignText-base);
	}

	&__body {
		padding: 8px 0;
	}
}

.bi-billable-block {
	& + .bi-billable-block {
		border-top: 1px solid var(--v-newDesignDivider-base, #e4e4e4);
	}
}

.bi-row {
	display: grid;
	grid-template-columns:
		28px
		minmax(140px, 1.4fr)
		minmax(100px, 0.85fr)
		minmax(100px, 0.85fr)
		minmax(56px, 0.55fr)
		minmax(70px, 0.65fr)
		minmax(130px, 1fr);
	gap: 6px;
	align-items: center;
	padding: 6px 16px;

	&--clickable {
		cursor: pointer;

		&:hover {
			background: rgba(0, 0, 0, 0.02);
		}
	}
}

.bi-header {
	align-items: center;
	padding-top: 2px;
	padding-bottom: 6px;

	&__cell {
		font-size: 0.78rem;
		font-weight: 500;
		color: var(--v-newDesignText-primary);
		opacity: 0.65;
		white-space: nowrap;

		&--billed {
			display: inline-flex;
			align-items: center;
			gap: 6px;
		}
	}
}

.bi-cell {
	min-width: 0;
}

.bi-cell--expand {
	display: flex;
	align-items: center;
	justify-content: center;
}

.bi-value {
	font-size: 0.85rem;
	color: var(--v-newDesignText-base);
	line-height: 1.25;
	min-height: 28px;
	display: flex;
	align-items: center;

	&--billed {
		gap: 6px;
		flex-wrap: nowrap;
	}
}

.bi-projects {
	display: flex;
	flex-wrap: wrap;
	gap: 4px;
	margin-top: 4px;
}

.bi-currency-flag {
	flex-shrink: 0;
}

.bi-currency-code {
	font-size: 0.78rem;
	opacity: 0.75;
	font-weight: 600;
}

.project {
	display: inline-flex;
	align-items: center;
	border-radius: 15px;
	height: 22px;
	max-width: 100%;

	.letter {
		width: 22px;
		height: 22px;
		display: inline-flex;
		align-items: center;
		justify-content: center;
		color: white;
		border-radius: 15px;
		font-size: 0.65rem;
		flex-shrink: 0;
	}

	.name {
		padding: 0 8px 0 4px;
		color: black;
		font-size: 0.72rem;
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
	}
}

.suggested-lines {
	padding: 8px 16px 12px 44px;
	background: rgba(0, 0, 0, 0.015);
	border-top: 1px solid var(--v-newDesignDivider-base, #e4e4e4);
}

.suggested-lines__header,
.suggested-lines__row {
	display: grid;
	grid-template-columns:
		36px
		minmax(150px, 1.2fr)
		minmax(220px, 1.8fr)
		minmax(80px, 0.7fr)
		minmax(90px, 0.8fr)
		minmax(120px, 1fr);
	gap: 6px;
	align-items: center;
	padding: 4px 0;
}

.suggested-lines__header {
	font-size: 0.75rem;
	font-weight: 500;
	opacity: 0.65;
	padding-bottom: 6px;
}

.assignee-block {
	& + .assignee-block {
		margin-top: 8px;
		padding-top: 4px;
		border-top: 1px solid var(--v-newDesignDivider-base, #e4e4e4);
	}
}

.suggested-lines__row {
	font-size: 0.85rem;

	&--assignee {
		padding: 6px 0;
	}

	&--status-follow {
		padding-top: 2px;
	}

	&--period {
		padding-left: 0;
		font-size: 0.8rem;
	}
}

.amount-value {
	font-weight: 700;

	&--ok {
		color: var(--v-success-base);
	}

	&--billed {
		color: var(--v-primary-base);
	}

	&--off {
		color: var(--v-error-base);
	}
}

.confirm-bi-meta {
	display: grid;
	grid-template-columns: 1fr 1fr auto;
	gap: 16px;
	align-items: start;

	&__label {
		color: var(--v-newDesignText-secondary, #888);
		font-size: 0.85rem;
		margin-bottom: 4px;
	}
}

.created-banner {
	color: var(--v-success-base);
	width: 100%;
	background: #e1f8dd;
	padding: 10px;
	text-align: center;
	border-radius: 5px;
	font-weight: 600;
	font-size: 1.05em;
}

.confirm-bi-panel {
	margin-bottom: 12px !important;
}

.suggested-lines__cell--check {
	display: flex;
	justify-content: center;
}

.suggested-lines__cell--assignee {
	display: flex;
	align-items: center;
	gap: 8px;
	min-height: 32px;
}

.assignee-avatar {
	flex-shrink: 0;
}

.assignee-meta {
	display: flex;
	flex-direction: column;
	align-items: flex-start;
	gap: 2px;
	min-width: 0;
}

.assignee-name {
	font-weight: 600;
	font-size: 0.85rem;
	line-height: 1.2;
}

.assignee-seat {
	display: inline-flex;
	align-items: center;
	padding: 0 8px;
	height: 18px;
	border-radius: 10px;
	background: rgba(33, 150, 243, 0.12);
	color: #1976d2;
	font-size: 0.7rem;
	font-weight: 600;
	white-space: nowrap;
	max-width: 100%;
	overflow: hidden;
	text-overflow: ellipsis;
}

.period-chips {
	display: inline-flex;
	flex-direction: column;
	align-items: stretch;
	gap: 4px;
	max-width: 100%;
}

.period-chip {
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 5px;
	box-sizing: border-box;
	padding: 2px 4px 2px 8px;
	border-radius: 12px;
	font-size: 0.68rem;
	font-weight: 600;
	line-height: 1.4;
	white-space: nowrap;
	color: var(--v-newDesignText-base);

	&__dates {
		padding-right: 2px;
	}

	&__badge {
		display: inline-flex;
		align-items: center;
		flex: 0 0 auto;
		padding: 0 7px;
		height: 16px;
		border-radius: 9px;
		color: #fff;
		font-size: 0.62rem;
		font-weight: 700;
		letter-spacing: 0.02em;
		text-transform: uppercase;
	}

	/* Billable: estilo que antes tenía Billed (borde verde + badge) */
	&--billable {
		background: rgba(76, 175, 80, 0.12);
		border: 1px solid rgba(76, 175, 80, 0.55);

		.period-chip__badge {
			background: var(--v-success-base);
		}
	}

	/* Billed: primary azul */
	&--billed {
		background: rgba(11, 126, 244, 0.12);
		border: 1px solid rgba(11, 126, 244, 0.55);

		.period-chip__badge {
			background: var(--v-primary-base);
		}
	}

	/* Non Billable: rojo + badge */
	&--off {
		background: rgba(244, 67, 54, 0.12);
		border: 1px solid rgba(244, 67, 54, 0.55);

		.period-chip__badge {
			background: var(--v-error-base);
		}
	}
}

.suggested-lines__cell--amount {
	display: inline-flex;
	align-items: center;
	gap: 6px;
	flex-wrap: nowrap;
}
</style>
